import type {ChatModelAdapter} from "@assistant-ui/react";
import {chatWithAiPersona, type AiPersona, type EventContext} from "./personas-api";

/**
 * Configuration for the persona chat adapter
 */
export interface PersonaChatAdapterConfig {
  /** The user profile to chat with */
  profile: AiPersona;
  /** Optional event context for location/date filtering */
  context?: EventContext;
}

/**
 * ChatModelAdapter that connects to the backend /api/chat/profile endpoint.
 * Returns a single complete response with supporting mentions in metadata.
 *
 * Best practices implemented:
 * - Proper abort signal handling for cancellation
 * - Error handling with user-friendly messages
 * - Type-safe message conversion
 */
export const createPersonaChatAdapter = (config: PersonaChatAdapterConfig): ChatModelAdapter => ({
  async run({messages, abortSignal}) {
    try {
      // Convert messages to backend format
      const backendMessages = messages.map((msg) => ({
        role: msg.role as "user" | "assistant" | "system",
        content: msg.content
          .filter((c) => c.type === "text")
          .map((c) => (c as {type: "text"; text: string}).text)
          .join("\n"),
      }));

      // Make request to backend profile chat endpoint
      const response = await chatWithAiPersona(
        {
          messages: backendMessages,
          profile: config.profile,
          context: config.context,
        },
        abortSignal
      );

      // Return the complete response with supporting mentions in metadata
      return {
        content: [{type: "text" as const, text: response.message}],
        metadata: {
          custom: {
            supporting_mentions: response.supporting_mentions || [],
          },
        },
      };
    } catch (error) {
      // Handle user cancellation gracefully - this is normal behavior
      if (error instanceof Error && error.name === "AbortError") {
        return {content: []};
      }
      // Re-throw other errors to display in UI
      throw error;
    }
  },
});
